// Shared by Cloud Functions and the frontend (imported there as @shared).
// Who is in line for the postseason: each conference's teams in seeding
// order and how many of them get in (college: the conference tournament;
// pro: the playoffs). startPostseason seeds from the same order, so the
// race managers watch during the season is the one that sets the field.

import {
  bracketSize, conferenceTournamentField, isPowerOfTwo,
} from "./leagueTypes.js";
import {compareConference, compareOverall} from "./standings.js";

/**
 * How pro playoff spots are handed out: an equal share per conference when
 * the field divides evenly and every conference can fill its share, else
 * the best records league-wide.
 * @param {object} league
 * @return {object} {byConference, perConference}
 */
export function playoffSpots(league) {
  const field = league.settings.postseason.fieldSize;
  const conferences = Object.values(league.conferences ?? {});
  const per = field / conferences.length;
  const byConference = conferences.length > 1 && Number.isInteger(per) &&
    per >= 2 && conferences.every((c) => c.teamIds.length >= per);
  return {byConference, perConference: byConference ? per : null};
}

/**
 * Whether each conference plays its own side of the pro bracket (its
 * champion reaches the Finals): spots go by conference and the number of
 * conferences is a power of 2. Otherwise qualifiers are seeded league-wide.
 * @param {object} league
 * @return {boolean}
 */
export function conferenceBrackets(league) {
  return playoffSpots(league).byConference &&
    isPowerOfTwo(Object.keys(league.conferences).length);
}

/**
 * The postseason race. Each group lists its teams best first:
 * {id, name, kind, teamIds, cutoff, byes}
 * - kind: "conference-tournament" (college), "auto-bid" (college without
 *   conference tournaments: the regular-season champion's automatic bid)
 *   or "playoffs" (pro);
 * - cutoff: how many of teamIds get in;
 * - byes: how many of the top seeds skip the first round (null when the
 *   seeding mixes groups, so it isn't known per group).
 * Pro leagues that hand out spots league-wide get one group, id "league".
 * @param {object} league
 * @param {object} teams {teamId: team document or summary row}
 * @return {object[]} groups
 */
export function postseasonRace(league, teams) {
  const ps = league.settings.postseason;
  const conferences = Object.values(league.conferences ?? {});
  const ordered = (ids, compare) =>
    ids.map((id) => teams[id]).filter(Boolean).sort(compare)
        .map((t) => t.id);

  if (ps.format === "series") {
    const {byConference, perConference} = playoffSpots(league);
    if (!byConference) {
      const cutoff = Math.min(ps.fieldSize, Object.keys(teams).length);
      return [{id: "league", name: "League", kind: "playoffs",
        teamIds: ordered(Object.keys(teams), compareOverall), cutoff,
        byes: bracketSize(cutoff) - cutoff}];
    }
    const split = conferenceBrackets(league);
    return conferences.map((c) => ({
      id: c.id, name: c.name, kind: "playoffs",
      teamIds: ordered(c.teamIds, compareOverall), cutoff: perConference,
      byes: split ? bracketSize(perConference) - perConference : null,
    }));
  }

  return conferences.map((c) => {
    const teamIds = ordered(c.teamIds, compareConference);
    if (!ps.conferenceTournaments) {
      return {id: c.id, name: c.name, kind: "auto-bid", teamIds, cutoff: 1,
        byes: null};
    }
    const cutoff = conferenceTournamentField(teamIds.length,
        ps.conferenceTournamentSize);
    return {id: c.id, name: c.name, kind: "conference-tournament", teamIds,
      cutoff, byes: bracketSize(cutoff) - cutoff};
  });
}

/**
 * Games between two teams' places in the standings, by the records that
 * order the group (conference record for college): positive when `a` is
 * ahead.
 * @param {object} a record
 * @param {object} b record
 * @param {boolean} conference compare conference records
 * @return {number} in half games
 */
export function gamesAhead(a, b, conference) {
  const [aw, al, bw, bl] = conference ?
    [a.confW, a.confL, b.confW, b.confL] : [a.w, a.l, b.w, b.l];
  return ((aw - bw) + (bl - al)) / 2;
}
